import * as THREE from 'three'
import type { SogsData } from '@/plugins/gsplat/core/SogsData'

/**
 * GPU 打包后的纹理结构
 */
export interface PackedTextures {
  /** 打包后的数据纹理A（位置 + 旋转.xy）- RGBA32UI */
  dataTextureA: THREE.DataTexture
  /** 打包后的数据纹理B（缩放 + 旋转.z）- RGBA16F */
  dataTextureB: THREE.DataTexture
  /** 打包后的颜色纹理（颜色RGB + 不透明度）- RGBA16F */
  colorTexture: THREE.DataTexture
  /** 纹理宽度 */
  width: number
  /** 纹理高度 */
  height: number
}

// SH 常量
const SH_C0 = 0.28209479177387814

/**
 * 将 float32 转换为 half float (16位)
 */
function float2Half(value: number): number {
  const floatView = new Float32Array(1)
  const int32View = new Int32Array(floatView.buffer)

  floatView[0] = value
  const x = int32View[0]

  let bits = (x >> 16) & 0x8000 // 符号位
  let m = (x >> 12) & 0x07ff // 尾数
  const e = (x >> 23) & 0xff // 指数

  if (e < 103) {
    return bits
  }

  if (e > 142) {
    bits |= 0x7c00
    bits |= (e === 255 ? 0 : 1) && x & 0x007fffff
    return bits
  }

  if (e < 113) {
    m |= 0x0800
    bits |= (m >> (114 - e)) + ((m >> (113 - e)) & 1)
    return bits
  }

  bits |= ((e - 112) << 10) | (m >> 1)
  bits += m & 1
  return bits
}

/**
 * SOGS GPU 打包器
 *
 * 参考 PlayCanvas gsplat-sogs-data.js 的 packGpuMemory() 实现
 *
 * 将 SOGS 压缩数据解码并打包为与 GaussianSplatGeometry 相同的纹理格式：
 * - dataTextureA: RGBA32UI (position.xyz + rotation.xy packed)
 * - dataTextureB: RGBA16F (scale.xyz + rotation.z)
 * - colorTexture: RGBA16F (color.rgb + opacity)
 */
export class SogsGpuPacker {
  constructor(_renderer: THREE.WebGLRenderer) {
    // renderer 保留以便将来实现 GPU 加速
  }

  /**
   * 执行 GPU 打包
   * @param sogsData SOGS 压缩数据
   * @returns 打包后的紧凑纹理
   */
  packGpuMemory(sogsData: SogsData): PackedTextures {
    // 检查数据是否可用
    if (
      !sogsData.meansLow ||
      !sogsData.meansHigh ||
      !sogsData.quats ||
      !sogsData.scales ||
      !sogsData.sh0
    ) {
      throw new Error('[SogsGpuPacker] SOGS 数据不可用（可能处于 minimalMemory 模式）')
    }

    // 使用与 SogsData 相同的纹理尺寸（避免 texSubImage2D 错误）
    const width = sogsData.textureWidth
    const height = sogsData.textureHeight

    // 使用 CPU 解码打包
    return this.packCPU(sogsData, width, height)
  }

  /**
   * CPU 解码打包实现
   * 输出格式与 GaussianSplatGeometry 完全一致
   */
  private packCPU(sogsData: SogsData, width: number, height: number): PackedTextures {
    const numSplats = sogsData.numSplats
    const size = width * height

    // 分配输出缓冲区 - 与 GaussianSplatGeometry 格式完全一致
    const dataA = new Uint32Array(size * 4) // RGBA32UI
    const dataAFloat = new Float32Array(dataA.buffer) // 用于写入 position
    const dataB = new Uint16Array(size * 4) // RGBA16F (half float)
    const colorData = new Uint16Array(size * 4) // RGBA16F (half float)

    const meansLow = sogsData.meansLow!
    const meansHigh = sogsData.meansHigh!
    const quats = sogsData.quats!
    const scales = sogsData.scales!
    const sh0 = sogsData.sh0!
    const meta = sogsData.meta

    // 解码每个 splat
    for (let i = 0; i < numSplats; i++) {
      const pixelOffset = i * 4
      const outOffset = i * 4

      // ========================================
      // 1. 解码位置（16-bit 精度，指数反变换）
      // ========================================
      const lowR = meansLow[pixelOffset]
      const lowG = meansLow[pixelOffset + 1]
      const lowB = meansLow[pixelOffset + 2]
      const highR = meansHigh[pixelOffset]
      const highG = meansHigh[pixelOffset + 1]
      const highB = meansHigh[pixelOffset + 2]

      const px16 = ((highR << 8) | lowR) / 65535
      const py16 = ((highG << 8) | lowG) / 65535
      const pz16 = ((highB << 8) | lowB) / 65535

      const nx = this.lerp(meta.means.mins[0], meta.means.maxs[0], px16)
      const ny = this.lerp(meta.means.mins[1], meta.means.maxs[1], py16)
      const nz = this.lerp(meta.means.mins[2], meta.means.maxs[2], pz16)

      // 指数反变换
      const x = Math.sign(nx) * (Math.exp(Math.abs(nx)) - 1)
      const y = Math.sign(ny) * (Math.exp(Math.abs(ny)) - 1)
      const z = Math.sign(nz) * (Math.exp(Math.abs(nz)) - 1)

      // ========================================
      // 2. 解码四元数（PlayCanvas SOGS 格式）
      // 参考 gsplat-sogs-data.js 第 83-95 行
      // RGB 通道存储 3 个分量（-0.5 偏移 + sqrt(2) 缩放）
      // Alpha 通道存储 mode（252-255），指示哪个分量被省略
      // ========================================
      const norm = Math.SQRT2
      const a = (quats[pixelOffset] / 255 - 0.5) * norm
      const b = (quats[pixelOffset + 1] / 255 - 0.5) * norm
      const c = (quats[pixelOffset + 2] / 255 - 0.5) * norm
      const d = Math.sqrt(Math.max(0, 1 - (a * a + b * b + c * c)))
      const mode = quats[pixelOffset + 3] - 252

      // 根据 mode 重建四元数 (x, y, z, w)
      // PlayCanvas r.set() 参数顺序是 (x, y, z, w)
      let qx: number, qy: number, qz: number, qw: number
      switch (mode) {
        case 0:
          qx = a
          qy = b
          qz = c
          qw = d
          break
        case 1:
          qx = d
          qy = b
          qz = c
          qw = a
          break
        case 2:
          qx = b
          qy = d
          qz = c
          qw = a
          break
        default:
          qx = b
          qy = c
          qz = d
          qw = a
          break
      }

      // 确保 w >= 0（四元数双覆盖）
      if (qw < 0) {
        qx = -qx
        qy = -qy
        qz = -qz
        qw = -qw
      }

      // ========================================
      // 3. 解码缩放
      // ========================================
      let scale_0: number, scale_1: number, scale_2: number

      if (meta.version === 2 && meta.scales.codebook) {
        const codebook = meta.scales.codebook
        scale_0 = codebook[scales[pixelOffset]] ?? -10
        scale_1 = codebook[scales[pixelOffset + 1]] ?? -10
        scale_2 = codebook[scales[pixelOffset + 2]] ?? -10
      } else if (meta.scales.mins && meta.scales.maxs) {
        scale_0 = this.lerp(meta.scales.mins[0], meta.scales.maxs[0], scales[pixelOffset] / 255)
        scale_1 = this.lerp(meta.scales.mins[1], meta.scales.maxs[1], scales[pixelOffset + 1] / 255)
        scale_2 = this.lerp(meta.scales.mins[2], meta.scales.maxs[2], scales[pixelOffset + 2] / 255)
      } else {
        scale_0 = scale_1 = scale_2 = -10
      }

      // ========================================
      // 4. 解码颜色和透明度
      // 参考 PlayCanvas gsplat-sogs-data.js 第 112-136 行
      // V2 和 V1 的 codebook/mins-maxs 都存储 SH DC 系数
      // 输出格式：0.5 + f_dc * SH_C0（RGB 空间）
      // ========================================
      let colorR: number, colorG: number, colorB: number, alpha: number

      if (meta.version === 2 && meta.sh0.codebook) {
        // V2: Codebook 直接存储 SH DC 系数
        const codebook = meta.sh0.codebook
        const idx0 = sh0[pixelOffset]
        const idx1 = sh0[pixelOffset + 1]
        const idx2 = sh0[pixelOffset + 2]
        const _idx3 = sh0[pixelOffset + 3]
        const f_dc_0 = codebook[idx0] ?? 0
        const f_dc_1 = codebook[idx1] ?? 0
        const f_dc_2 = codebook[idx2] ?? 0

        // 转换为 RGB 空间：0.5 + f_dc * SH_C0
        // 注意：需要 clamp 到 [0, 1]，因为 codebook 值范围可能超出标准范围
        colorR = Math.max(0, Math.min(1, 0.5 + f_dc_0 * SH_C0))
        colorG = Math.max(0, Math.min(1, 0.5 + f_dc_1 * SH_C0))
        colorB = Math.max(0, Math.min(1, 0.5 + f_dc_2 * SH_C0))

        // V2: alpha 直接使用，不需要 sigmoid
        alpha = sh0[pixelOffset + 3] / 255
      } else if (meta.sh0.mins && meta.sh0.maxs) {
        // V1: mins/maxs 存储的是 SH DC 系数的范围
        const f_dc_0 = this.lerp(meta.sh0.mins[0], meta.sh0.maxs[0], sh0[pixelOffset] / 255)
        const f_dc_1 = this.lerp(meta.sh0.mins[1], meta.sh0.maxs[1], sh0[pixelOffset + 1] / 255)
        const f_dc_2 = this.lerp(meta.sh0.mins[2], meta.sh0.maxs[2], sh0[pixelOffset + 2] / 255)
        const a = this.lerp(meta.sh0.mins[3], meta.sh0.maxs[3], sh0[pixelOffset + 3] / 255)
        // 转换为 RGB 空间：0.5 + f_dc * SH_C0，并 clamp 到 [0, 1]
        colorR = Math.max(0, Math.min(1, 0.5 + f_dc_0 * SH_C0))
        colorG = Math.max(0, Math.min(1, 0.5 + f_dc_1 * SH_C0))
        colorB = Math.max(0, Math.min(1, 0.5 + f_dc_2 * SH_C0))
        // V1: alpha 需要 sigmoid 转换（a 是 logit 空间）
        alpha = 1.0 / (1.0 + Math.exp(-a))
      } else {
        colorR = colorG = colorB = 0.5
        alpha = 1.0
      }

      // ========================================
      // 写入打包数据 - 与 GaussianSplatGeometry 完全一致
      // ========================================

      // dataA: RGBA32UI
      // - RGB: position.xyz (float32 bits as uint32)
      // - A: rotation.xy packed (half | half << 16)
      dataAFloat[outOffset + 0] = x
      dataAFloat[outOffset + 1] = y
      dataAFloat[outOffset + 2] = z
      dataA[outOffset + 3] = float2Half(qx) | (float2Half(qy) << 16)

      // dataB: RGBA16F
      // - RGB: scale.xyz (half, exp activated)
      // - A: rotation.z (half)
      dataB[outOffset + 0] = float2Half(Math.exp(scale_0))
      dataB[outOffset + 1] = float2Half(Math.exp(scale_1))
      dataB[outOffset + 2] = float2Half(Math.exp(scale_2))
      dataB[outOffset + 3] = float2Half(qz)

      colorData[outOffset + 0] = float2Half(colorR)
      colorData[outOffset + 1] = float2Half(colorG)
      colorData[outOffset + 2] = float2Half(colorB)
      colorData[outOffset + 3] = float2Half(alpha)
    }

    // 创建纹理 - 与 GaussianSplatGeometry 格式完全一致

    // dataTextureA: RGBA32UI
    const dataTextureA = new THREE.DataTexture(
      dataA,
      width,
      height,
      THREE.RGBAIntegerFormat,
      THREE.UnsignedIntType
    )
    dataTextureA.internalFormat = 'RGBA32UI'
    dataTextureA.minFilter = THREE.NearestFilter
    dataTextureA.magFilter = THREE.NearestFilter
    dataTextureA.wrapS = THREE.ClampToEdgeWrapping
    dataTextureA.wrapT = THREE.ClampToEdgeWrapping
    dataTextureA.needsUpdate = true

    // dataTextureB: RGBA16F
    const dataTextureB = new THREE.DataTexture(
      dataB,
      width,
      height,
      THREE.RGBAFormat,
      THREE.HalfFloatType
    )
    dataTextureB.internalFormat = 'RGBA16F'
    dataTextureB.minFilter = THREE.NearestFilter
    dataTextureB.magFilter = THREE.NearestFilter
    dataTextureB.wrapS = THREE.ClampToEdgeWrapping
    dataTextureB.wrapT = THREE.ClampToEdgeWrapping
    dataTextureB.needsUpdate = true

    // colorTexture: RGBA16F
    const colorTexture = new THREE.DataTexture(
      colorData,
      width,
      height,
      THREE.RGBAFormat,
      THREE.HalfFloatType
    )
    colorTexture.internalFormat = 'RGBA16F'
    colorTexture.colorSpace = THREE.LinearSRGBColorSpace
    colorTexture.minFilter = THREE.NearestFilter
    colorTexture.magFilter = THREE.NearestFilter
    colorTexture.wrapS = THREE.ClampToEdgeWrapping
    colorTexture.wrapT = THREE.ClampToEdgeWrapping
    colorTexture.needsUpdate = true

    return {
      dataTextureA,
      dataTextureB,
      colorTexture,
      width,
      height,
    }
  }

  private lerp(a: number, b: number, t: number): number {
    return a * (1 - t) + b * t
  }

  /**
   * 清理资源
   */
  dispose(): void {
    // CPU 实现无需清理
  }
}
